<template>
  <div
    class="relative flex flex-col gap-12"
    :class="{
      'after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:right-0 after:h-36 after:bg-gradient-to-t after:from-[#f9f9f9] after:to-transparent':
        variant === 'default' && !arrivedState.bottom,
    }"
  >
    <div class="flex items-center gap-x-8">
      <h3
        v-if="variant === 'default' || displayInfos"
        class="body-1-bold line-clamp-1 overflow-hidden text-ellipsis"
      >
        {{ title }}
      </h3>

      <RevButtonBase
        v-if="clearable && selectedValues.length > 0"
        class="text-action-default-hi body-2-bold ml-auto px-12 py-2"
        size="small"
        variant="primary"
        @click="clear"
        >{{ i18n(translations.clear) }}</RevButtonBase
      >
    </div>

    <div v-if="values.length > 9" class="mb-4">
      <RevInputText
        :id="`input-${name}`"
        v-model="searchQuery"
        aria-describedby="n-filter-options"
        :disabled="!ready"
        :icon="IconSearch"
        :label="i18n(translations.productFilterSearch, { facet: title })"
        type="search"
      />
      <div
        id="n-filter-options"
        aria-live="polite"
        class="sr-only"
        role="status"
      >
        {{
          i18n(translations.foundFilterOptions, {
            count: filteredOptions.length,
          })
        }}
      </div>
    </div>

    <ul
      v-if="type === 'checkbox'"
      ref="list"
      class="rounded-md flex flex-col gap-16"
      :class="{
        'max-h-[17rem] overflow-y-auto overflow-x-hidden':
          variant === 'default',
      }"
    >
      <li v-if="variant === 'default'">
        <RevCheckbox
          :id="`reset-${name}`"
          :disabled="!ready || selectedValues.length === 0"
          :model-value="selectedValues.length === 0"
          @update:model-value="selectedValues = []"
        >
          <template #label>
            <span class="body-1">{{
              i18n(translations.productFilterNone)
            }}</span>
          </template>
        </RevCheckbox>
      </li>

      <li v-for="value in filteredOptions" :key="value.name">
        <RevCheckbox
          :id="`${name}-${value.name}`"
          v-model="selectedValues"
          :disabled="!ready"
          :value="value.name"
          :variant="variant === 'default' ? 'default' : 'full'"
        >
          <template #label>
            <span>
              {{ value.label }}
            </span>
          </template>
        </RevCheckbox>
      </li>
    </ul>

    <ul
      v-if="type === 'radio'"
      class="flex flex-col gap-16"
      :class="{ 'overflow-y-auto overflow-x-hidden': variant === 'default' }"
    >
      <li v-if="variant === 'default'">
        <RevRadio
          :id="`reset-${name}`"
          :disabled="!ready || selectedValues.length === 0"
          :model-value="selectedValues.length === 0"
          :value="'reset'"
          @change="selectedValues = []"
        >
          <template #label>
            <span class="body-1">{{
              i18n(translations.productFilterNone)
            }}</span>
          </template>
        </RevRadio>
      </li>
      <li v-for="value in filteredOptions" :key="value.name">
        <RevRadio
          :id="`${name}-${value.name}`"
          :disabled="!ready"
          :model-value="selectedValues[0]"
          :value="value.name"
          :variant="variant === 'default' ? 'default' : 'full'"
          @update:model-value="(val) => (selectedValues = [val as string])"
        >
          <template #label>
            <span>
              {{ value.label }}
            </span>
          </template>
        </RevRadio>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevInputText } from '@ds/components/InputText'
import { RevRadio } from '@ds/components/Radio'
import { IconSearch } from '@ds/icons/IconSearch'
import { useScroll, watchDebounced } from '@vueuse/core'

import { useFilterTracking } from '../../composables/useFilterTracking'

import translations from './FacetFilter.translations'

const list = useTemplateRef<HTMLElement>('list')
const { arrivedState } = useScroll(list)

const props = defineProps<{
  title: string
  name: string
  type: 'radio' | 'checkbox'
  values: Array<{ label: string; name: string }>
  variant: 'full' | 'default'
  displayInfos?: boolean
  clearable?: boolean
}>()

const i18n = useI18n()
const { trackFilterClick } = useFilterTracking()

const searchQuery = ref('')
const ready = ref(false)
const filteredOptions = ref<{ label: string; name: string }[]>([])

const selectedValues = defineModel<string[]>({
  required: true,
})

onMounted(() => {
  ready.value = true
})

const refreshOptions = () => {
  filteredOptions.value = props.values
    .filter((value) =>
      value.name
        .toLocaleLowerCase()
        .includes(searchQuery.value.toLocaleLowerCase().trim()),
    )
    .filter((item) => item.label)
}

watchDebounced(searchQuery, refreshOptions, { debounce: 300 })
watch(() => props.values, refreshOptions, { immediate: true, deep: true })

watch(selectedValues, (newInput, oldInput) => {
  if (newInput.length === 0) {
    trackFilterClick({
      name: props.name,
      zone: 'filter',
      value: 'All',
    })
  } else {
    const value = newInput
      .filter((x) => !oldInput.includes(x))
      .concat(oldInput.filter((x) => !newInput.includes(x)))[0]

    const valueLabel = filteredOptions.value.find(
      (i) => i.name === value,
    )?.label
    trackFilterClick({
      name: props.name,
      zone: 'filter',
      value: valueLabel ?? value,
    })
  }
})

function clear() {
  selectedValues.value = []
}
</script>
